import React, { useState } from 'react';
import EditEmployeeDialog from './EditEmployeeDialog';
import { Employee } from '../../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as AddressIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  ContactPhone as EmergencyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Schedule as TimeIcon,
  Assessment as PerformanceIcon
} from '@mui/icons-material';

interface EmployeeDetailsDialogProps {
  open: boolean;
  employee: Employee | null;
  onClose: () => void;
  onEdit: (employee: EmployeeExtended) => void;
  onDelete: (employeeId: number) => void;
  onTerminate: (employeeId: number) => void;
}

// Extended interface for the dialog with additional properties
interface EmployeeExtended extends Employee {
  employee_number?: string;
  date_of_birth?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  work_location?: string;
  employee_type?: string;
  probation_end_date?: string;
  payroll_frequency?: string;
  bank_name?: string;
  account_holder_name?: string;
  routing_number?: string;
  account_number?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  direct_reports_count?: number;
  social_security_number?: string;
  student_loan?: boolean;
}

const EmployeeDetailsDialog: React.FC<EmployeeDetailsDialogProps> = ({
  open,
  employee,
  onClose,
  onEdit,
  onDelete,
  onTerminate
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Cast employee to extended type for additional properties
  const emp = employee as EmployeeExtended | null;

  if (!employee) return null;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveEdit = (updatedEmployee: EmployeeExtended) => {
    onEdit(updatedEmployee);
    setEditDialogOpen(false);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'on_leave': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon color="primary" />
            <Box>
              <Typography variant="h5">
                {employee.first_name} {employee.last_name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {emp?.employee_number} • {employee.job_title}
              </Typography>
            </Box>
            <Chip
              label={employee.employment_status.charAt(0).toUpperCase() + employee.employment_status.slice(1)}
              color={getStatusColor(employee.employment_status) as any}
              icon={employee.employment_status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
            />
          </Box>
          <IconButton onClick={handleMenuClick}>
            <MoreIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
            <Tab label="Personal Details" />
            <Tab label="Employment" />
            <Tab label="Payroll & Tax" />
            <Tab label="Performance" />
          </Tabs>
        </Box>

        {/* Personal Details Tab */}
        <TabPanel value={selectedTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Personal Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary="Email" secondary={employee.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary="Phone" secondary={employee.phone || 'N/A'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Date of Birth" secondary={formatDate(emp?.date_of_birth)} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddressIcon color="primary" />
                    Address
                  </Typography>
                  <Typography variant="body2">
                    {emp?.address_line_1 && (
                      <>
                        {emp?.address_line_1}<br />
                        {emp?.address_line_2 && <>{emp?.address_line_2}<br /></>}
                        {emp?.city && <>{emp?.city}<br /></>}
                        {emp?.county && <>{emp?.county}<br /></>}
                        {emp?.postcode}
                      </>
                    )}
                    {!emp?.address_line_1 && 'No address on file'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmergencyIcon color="primary" />
                    Emergency Contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Name</Typography>
                      <Typography variant="body2">{employee.emergency_contact_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Phone</Typography>
                      <Typography variant="body2">{employee.emergency_contact_phone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Relationship</Typography>
                      <Typography variant="body2">{emp?.emergency_contact_relationship || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Employment Tab */}
        <TabPanel value={selectedTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="primary" />
                    Employment Details
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Department" secondary={employee.department} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Job Title" secondary={employee.job_title} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Work Location" secondary={emp?.work_location ? emp.work_location.charAt(0).toUpperCase() + emp.work_location.slice(1) : 'N/A'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Employment Type" secondary={emp?.employee_type ? emp.employee_type.charAt(0).toUpperCase() + emp.employee_type.slice(1) : 'N/A'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Direct Reports" secondary={emp?.direct_reports_count || 0} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Employment Dates</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Start Date" secondary={formatDate(employee.start_date)} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Probation End" secondary={formatDate(emp?.probation_end_date)} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="End Date" secondary={formatDate(employee.end_date)} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Payroll & Tax Tab */}
        <TabPanel value={selectedTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    Salary & Benefits
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Annual Salary"
                        secondary={employee.salary ? formatCurrency(employee.salary) : 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Hourly Rate"
                        secondary={employee.salary ? formatCurrency(employee.salary / 2080) + '/hour' : 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Payroll Frequency"
                        secondary={emp?.payroll_frequency ? (emp.payroll_frequency.charAt(0).toUpperCase() + emp.payroll_frequency.slice(1)) : 'Monthly'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Pension Scheme"
                        secondary={employee.pension_scheme || 'Not Enrolled'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon color="primary" />
                    US Tax Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Social Security Number"
                        secondary={emp?.social_security_number || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Tax Code"
                        secondary={employee.tax_code || '1257L'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Student Loan"
                        secondary={emp?.student_loan ? 'Yes' : 'No'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Bank Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2">Bank Name</Typography>
                      <Typography variant="body2">{emp?.bank_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2">Account Holder</Typography>
                      <Typography variant="body2">{emp?.account_holder_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2">Routing Number</Typography>
                      <Typography variant="body2">{emp?.routing_number || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2">Account Number</Typography>
                      <Typography variant="body2">{emp?.account_number ? '****' + emp.account_number.slice(-4) : 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={selectedTab} index={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Performance Management Coming Soon</Typography>
            <Typography variant="body2">
              • Goal setting and tracking<br />
              • Performance reviews and ratings<br />
              • 360-degree feedback<br />
              • Career development plans<br />
              • Training and certification tracking
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PerformanceIcon color="primary" />
                    Performance Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Performance data will be displayed here once the performance management system is implemented.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon color="primary" />
                    Recent Activity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent time entries, leave requests, and performance updates will be shown here.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleEdit} startIcon={<EditIcon />}>
          Edit Employee
        </Button>
      </DialogActions>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Employee
        </MenuItem>
        <MenuItem onClick={() => { onTerminate(employee.id); handleMenuClose(); }}>
          <InactiveIcon sx={{ mr: 1 }} />
          Terminate Employment
        </MenuItem>
        <MenuItem onClick={() => { onDelete(employee.id); handleMenuClose(); }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Employee
        </MenuItem>
      </Menu>

      {/* Edit Employee Dialog */}
      <EditEmployeeDialog
        open={editDialogOpen}
        employee={emp}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveEdit}
      />
    </Dialog>
  );
};

export default EmployeeDetailsDialog;
